import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Bell, Tv, Video } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { importedSiteImages } from '@/content/public-site-content';

export default function GMTVPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleNotify(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    try {
      await api.post('api/features/waitlist', { email: email.trim(), feature: 'gmtv' });
    } catch {
      // Keep the UX optimistic even if the endpoint is not available yet.
    } finally {
      setSubmitting(false);
      toast.success("You're on the list! We'll notify you when GMTV launches.");
      setEmail('');
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      <section className="overflow-hidden rounded-[2rem] border border-border/70 bg-card shadow-sm">
        <div className="grid gap-0 lg:grid-cols-[1fr_0.9fr]">
          <div className="space-y-5 p-8 sm:p-10">
            <Badge variant="outline" className="w-fit border-primary/30 bg-primary/5">
              <Tv className="mr-1 h-3 w-3 text-primary" />
              Global Missions TV (GMTV)
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Global Missions TV (GMTV)
            </h1>
            <p className="text-base leading-7 text-muted-foreground sm:text-lg">
              Watch the GMTV rollout and stay ready for upcoming live and on-demand gospel media programming.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/media">Back to media center</Link>
              </Button>
            </div>
          </div>
          <div className="relative min-h-[280px] bg-muted">
            <img src={importedSiteImages.gmtvHero} alt="GMTV" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Live TV</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>Watch live church services and special broadcasts as they happen.</CardDescription>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">VOD Library</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>Stream teaching series, sermons, and worship sessions on demand.</CardDescription>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Global Reach</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>Content from ministry locations and mission activity across regions.</CardDescription>
          </CardContent>
        </Card>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Get notified
          </CardTitle>
          <CardDescription>Enter your email and we’ll let you know when GMTV goes live.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleNotify} className="flex flex-col gap-3 sm:flex-row">
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <Button type="submit" disabled={submitting}>
              <Video className="mr-2 h-4 w-4" />
              {submitting ? 'Saving...' : 'Notify Me'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
